import { useState } from 'react';
import BreadcrumbNav from '@/components/base/BreadcrumbNav';

export default function AboutPage() {
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const formUrl = 'https://readdy.ai/api/form/d9qogmfnahb7382ovcu0';

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setFormName('');
      setFormEmail('');
      setFormMessage('');
      return;
    }

    setFormStatus('submitting');
    try {
      const formData = new FormData(form);
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormName('');
        setFormEmail('');
        setFormMessage('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const schemaOrg = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '高潮喷水完全指南',
    url: 'https://cbhfyv.cn/',
    description: '致力于以科学、客观、尊重的态度传播性健康知识，帮助大众建立正确的身体认知与性健康意识。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@health-guide.cn',
      telephone: '400-123-4567',
      contactType: 'customer service',
    },
  };

  const schemaAbout = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 高潮喷水完全指南',
    url: 'https://cbhfyv.cn/about',
    description: '了解高潮喷水完全指南的品牌理念、专业团队和联系方式。',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaAbout) }}
      />
      <div className="pt-24 pb-16 md:pb-20 min-h-screen">
        <BreadcrumbNav
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <div className="w-full px-6 md:px-10">
          <div className="max-w-4xl mx-auto">
            {/* Brand Introduction */}
            <section className="mb-16">
              <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground-900">
                关于我们
              </h1>
              <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                高潮喷水完全指南是一个致力于科学性健康科普的专业平台。我们相信，正确的知识是消除困惑和羞耻感的最有力武器。通过提供基于循证医学的专业内容，我们希望帮助每一个人建立对自身身体的正确认知，培养积极的性健康意识。
              </p>
            </section>

            {/* Mission */}
            <section className="mb-16 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-focus-3-line text-xl"></i>
                </div>
                <h3 className="mt-4 text-lg font-heading font-semibold text-foreground-900">科学为本</h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                  所有内容均基于同行评审的医学研究和权威医学文献，确保信息的准确性和可靠性。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className="ri-shield-star-line text-xl"></i>
                </div>
                <h3 className="mt-4 text-lg font-heading font-semibold text-foreground-900">尊重包容</h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                  我们以尊重和包容的态度讨论性健康话题，拒绝任何形式的偏见、歧视和不实信息。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600">
                  <i className="ri-user-heart-line text-xl"></i>
                </div>
                <h3 className="mt-4 text-lg font-heading font-semibold text-foreground-900">赋能个体</h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                  我们致力于通过知识赋能每一个人，帮助读者建立自信、健康的身体认知和性态度。
                </p>
              </div>
            </section>

            {/* Professional Team */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-8">
                专业声誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 flex items-start gap-4">
                  <div className="w-14 h-14 flex-shrink-0 rounded-full overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20doctor%20portrait%20in%20white%20coat%2C%20warm%20friendly%20smile%2C%20clean%20medical%20office%20background%2C%20soft%20natural%20lighting%2C%20professional%20headshot%20photography&width=200&height=200&seq=doctor-li-2026&orientation=squarish"
                      alt="李医生"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900">李医生</h3>
                    <p className="text-xs text-foreground-400 mt-0.5">妇产科副主任医师</p>
                    <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                      毕业于北京大学医学部，从事妇产科临床工作15年。专注女性性健康与盆底医学研究，发表SCI论文20余篇。
                    </p>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 flex items-start gap-4">
                  <div className="w-14 h-14 flex-shrink-0 rounded-full overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20doctor%20portrait%20in%20white%20coat%2C%20warm%20confident%20expression%2C%20bright%20medical%20office%2C%20soft%20natural%20lighting%2C%20professional%20headshot%20photography&width=200&height=200&seq=doctor-wang-2026&orientation=squarish"
                      alt="王医生"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900">王医生</h3>
                    <p className="text-xs text-foreground-400 mt-0.5">泌尿外科主任医师</p>
                    <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                      复旦大学上海医学院博士，专攻女性泌尿与盆底重建。在女性性功能障碍的临床诊疗方面拥有丰富经验。
                    </p>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 flex items-start gap-4">
                  <div className="w-14 h-14 flex-shrink-0 rounded-full overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20doctor%20portrait%20in%20white%20coat%2C%20gentle%20smile%2C%20modern%20medical%20office%2C%20soft%20natural%20lighting%2C%20professional%20headshot%20photography&width=200&height=200&seq=doctor-chen-2026&orientation=squarish"
                      alt="陈医生"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900">陈医生</h3>
                    <p className="text-xs text-foreground-400 mt-0.5">心理学博士 / 性治疗师</p>
                    <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                      中国科学院心理研究所博士，持有国际性教育及性治疗师认证。擅长帮助来访者处理性羞耻和性心理障碍。
                    </p>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 flex items-start gap-4">
                  <div className="w-14 h-14 flex-shrink-0 rounded-full overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20doctor%20portrait%20in%20white%20coat%2C%20academic%20expression%2C%20university%20office%20background%2C%20soft%20natural%20lighting%2C%20professional%20headshot%20photography&width=200&height=200&seq=doctor-zhao-2026&orientation=squarish"
                      alt="赵医生"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900">赵医生</h3>
                    <p className="text-xs text-foreground-400 mt-0.5">医学博士 / 医学科普作家</p>
                    <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                      浙江大学医学院博士，多家知名健康媒体的特约作者。致力于将复杂的医学知识转化为通俗易懂的科普内容。
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact Form */}
            <section>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-8">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-900">电子邮箱</h4>
                      <p className="text-sm text-foreground-500 mt-0.5">contact@health-guide.cn</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-900">客服热线</h4>
                      <p className="text-sm text-foreground-500 mt-0.5">400-123-4567</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-900">工作时间</h4>
                      <p className="text-sm text-foreground-500 mt-0.5">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3">
                  {formStatus === 'success' ? (
                    <div className="bg-accent-50 border border-accent-200/60 rounded-xl p-8 text-center">
                      <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <i className="ri-check-line text-xl"></i>
                      </div>
                      <p className="mt-4 text-sm font-medium text-accent-700">消息已发送成功！</p>
                      <p className="mt-1 text-xs text-accent-500">我们会尽快回复您。</p>
                    </div>
                  ) : (
                    <form
                      data-readdy-form="contact-form"
                      onSubmit={handleContactSubmit}
                      className="bg-background-50 border border-background-200/70 rounded-xl p-6 space-y-4"
                    >
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          value={formName}
                          onChange={(e) => setFormName(e.target.value)}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          value={formEmail}
                          onChange={(e) => setFormEmail(e.target.value)}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言（限500字）
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          value={formMessage}
                          onChange={(e) => setFormMessage(e.target.value)}
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                          placeholder="请输入您的留言..."
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="w-full py-3 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                      </button>
                      {formStatus === 'error' && (
                        <p className="text-xs text-red-500 text-center">{formError}</p>
                      )}
                    </form>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}